import { useState, useMemo, useCallback } from "react";
import { Tooltip, MenuItem, TextField, Box, Menu, Typography, Autocomplete } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useDebounce } from "utils/useDebounce";

/**
 * Custom hook for handling sorting and filtering in history tabs
 * @param {Array} filterOptions - Array of filter options { value, label }
 * @param {Object} dropdownOptions - Object mapping field names to their dropdown options
 *                                   e.g., { cost_center_id: [{ _id: '123', name: 'Cost Center 1' }] }
 * @param {Function} setCurrentPage - Optional setter to reset pagination to page 1 whenever the
 *                                    search/filter criteria change (otherwise a stale page number
 *                                    from a previous, larger result set can return an empty page).
 * @returns {Object} - State and handlers for sorting/filtering
 */
export const useTabSortFilter = (filterOptions = [], dropdownOptions = {}, setCurrentPage) => {
  const [filterField, setFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [nameSortMenuAnchor, setNameSortMenuAnchor] = useState(null);
  const [emailSearch, setEmailSearch] = useState("");
  const debouncedFilterValue = useDebounce(filterValue, 400);
  const debouncedEmailSearch = useDebounce(emailSearch, 400);

  // Reset to page 1 whenever the search/filter criteria change, so a stale page
  // number from a previous (larger) result set doesn't return an empty page.
  const resetPage = useCallback(() => {
    if (setCurrentPage) setCurrentPage(1);
  }, [setCurrentPage]);

  // Helper function to convert Date object or date string to YYYY-MM-DD for date filtering
  const formatDateForAPI = useCallback((dateValue) => {
    if (!dateValue) return "";
    
    // If it's a Date object from DatePicker
    if (dateValue instanceof Date && !isNaN(dateValue)) {
      return format(dateValue, "yyyy-MM-dd");
    }
    
    // If it's already a string, return as-is
    return dateValue;
  }, []);

  // Convert filter value - for dates/dropdowns use immediate value, for text use debounced
  const formattedFilterValue = useMemo(() => {
    if (filterField === "changed_date") {
      // For date fields, use filterValue directly (Date object from picker)
      return formatDateForAPI(filterValue);
    }
    if (dropdownOptions[filterField]) {
      // For dropdown fields (like cost_center_id), use filterValue directly (no debouncing)
      return filterValue;
    }
    // For text fields, use debounced value to prevent API calls on every keystroke
    return debouncedFilterValue;
  }, [filterField, debouncedFilterValue, filterValue, formatDateForAPI, dropdownOptions]);

  // Handle sorting - three-state toggle: none → asc → desc → none
  const handleSortClick = useCallback((field, setCurrentPage) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("");
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    if (setCurrentPage) setCurrentPage(1);
    setNameSortMenuAnchor(null);
  }, [sortBy, sortOrder]);

  const handleNameSortMenuOpen = useCallback((event) => {
    setNameSortMenuAnchor(event.currentTarget);
  }, []);

  const handleNameSortMenuClose = useCallback(() => {
    setNameSortMenuAnchor(null);
  }, []);

  // Direct toggle for single-field columns - three-state: none → asc → desc → none
  const handleDirectSort = useCallback((field, setCurrentPage) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("");
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    if (setCurrentPage) setCurrentPage(1);
  }, [sortBy, sortOrder]);

  // Render name sort menu (Email, First Name, Last Name)
  const renderNameSortMenu = useCallback((setCurrentPage) => (
    <Menu
      anchorEl={nameSortMenuAnchor}
      open={Boolean(nameSortMenuAnchor)}
      onClose={handleNameSortMenuClose}
    >
      <MenuItem onClick={() => handleSortClick("email", setCurrentPage)}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minWidth: 150 }}>
          <span>Email</span>
          {sortBy === "email" && (
            sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 2 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 2 }} />
          )}
        </Box>
      </MenuItem>
      <MenuItem onClick={() => handleSortClick("first_name", setCurrentPage)}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minWidth: 150 }}>
          <span>First Name</span>
          {sortBy === "first_name" && (
            sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 2 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 2 }} />
          )}
        </Box>
      </MenuItem>
      <MenuItem onClick={() => handleSortClick("last_name", setCurrentPage)}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minWidth: 150 }}>
          <span>Last Name</span>
          {sortBy === "last_name" && (
            sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 2 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 2 }} />
          )}
        </Box>
      </MenuItem>
    </Menu>
  ), [nameSortMenuAnchor, handleNameSortMenuClose, handleSortClick, sortBy, sortOrder]);

  // Render name sort icon button (for name column header)
  const renderNameSortIcon = useCallback(() => (

<Tooltip title="Sort users" placement="top">
      <IconButton
        className="sort-icon"
        size="small"
        onClick={handleNameSortMenuOpen}
        sx={{
          position: "absolute",
          right: 0,
          padding: "2px",
          minWidth: "28px",
          flexShrink: 0,
          opacity: 0,
          transition: "opacity 0.2s",
          color: (sortBy === "email" || sortBy === "first_name" || sortBy === "last_name") ? "#1976d2" : "#666"
        }}
      >
        {(sortBy === "email" || sortBy === "first_name" || sortBy === "last_name") ? (
          sortOrder === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
        ) : (
          <ArrowUpwardIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  ), [sortBy, sortOrder, handleNameSortMenuOpen]);

  // Render sort icon button for headers
  const renderSortIcon = useCallback((field, title) => (
    <Tooltip 
      title={
        sortBy === field 
          ? (sortOrder === "asc" ? "Click for descending" : "Click to clear sort") 
          : "Click to sort ascending"
      } 
      placement="top"
    >
      <IconButton
        className="sort-icon"
        size="small"
        onClick={() => handleDirectSort(field)}
        sx={{
          position: "absolute",
          right: 0,
          padding: "2px",
          minWidth: "28px",
          flexShrink: 0,
          opacity: 0,
          transition: "opacity 0.2s",
          color: sortBy === field ? "#1976d2" : "#666"
        }}
      >
        {sortBy === field && sortOrder === "desc" ? (
          <ArrowDownwardIcon fontSize="small" />
        ) : (
          <ArrowUpwardIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  ), [sortBy, sortOrder, handleDirectSort]);

  // Render inline "Filter By" label + field select (no icon/popover)
  const renderFilterFieldSelect = useMemo(() => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap" }}>
        Filter By
      </Typography>
      <TextField
        select
        size="small"
        value={filterField}
        onChange={(event) => {
          setFilterField(event.target.value);
          setFilterValue("");
          resetPage();
        }}
        sx={{
          minWidth: 160,
          "& .MuiInputBase-root": { height: "31px" },
        }}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">Email</MenuItem>
        {filterOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  ), [filterField, filterOptions, resetPage]);

  // Render dynamic search/filter input - switches based on filterField selection
  const renderDynamicSearchInput = useMemo(() => {
    // No filter field selected → show email search
    if (!filterField) {
      return (
        <TextField
          id="outlined-search"
          placeholder="Search By Email"
          type="search"
          variant="outlined"
          value={emailSearch}
          onChange={(e) => {
            setEmailSearch(e.target.value);
            resetPage();
          }}
          sx={{ minWidth: '200px' }}
          InputProps={{
            style: {
              height: "31px",
            },
          }}
        />
      );
    }

    // Filter field selected → show appropriate input
    if (filterField === "changed_date") {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label=""
            disableFuture
            value={filterValue || null}
            onChange={(newValue) => {
              setFilterValue(newValue);
              resetPage();
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="Select Date"
                variant="outlined"
                sx={{ minWidth: '200px' }}
                InputProps={{
                  ...params.InputProps,
                  style: { 
                    height: "31px",
                    fontSize: "inherit",
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    ...params.inputProps?.style,
                    padding: "5px 4px",
                    fontSize: "inherit",
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (dropdownOptions[filterField]) {
      const selectedFieldLabel = filterOptions.find(opt => opt.value === filterField)?.label || "Value";
      return (
        <Autocomplete
          options={dropdownOptions[filterField] || []}
          getOptionLabel={(option) => option.name || ""}
          value={dropdownOptions[filterField]?.find(opt => opt._id === filterValue) || null}
          onChange={(event, newValue) => {
            setFilterValue(newValue?._id || "");
            resetPage();
          }}
          sx={{ minWidth: '200px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Select ${selectedFieldLabel}`}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: { 
                  height: "31px",
                  fontSize: "inherit",
                }
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  ...params.inputProps?.style,
                  padding: "5px 4px",
                  fontSize: "inherit",
                }
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option._id === value._id}
        />
      );
    }

    // Default: text field with dynamic label based on selected field
    const selectedFieldLabel = filterOptions.find(opt => opt.value === filterField)?.label || "Value";
    return (
      <TextField
        placeholder={`Search By ${selectedFieldLabel}`}
        variant="outlined"
        type="search"
        value={filterValue}
        onChange={(event) => {
          setFilterValue(event.target.value);
          resetPage();
        }}
        sx={{ minWidth: '200px' }}
        InputProps={{
          style: {
            height: "31px",
          },
        }}
      />
    );
  }, [filterField, filterValue, emailSearch, dropdownOptions, filterOptions, resetPage]);

  return {
    // State
    filterField,
    filterValue,
    sortBy,
    sortOrder,
    debouncedFilterValue,
    formattedFilterValue,
    nameSortMenuAnchor,
    emailSearch,
    debouncedEmailSearch,
    
    // Handlers
    handleSortClick,
    handleDirectSort,
    handleNameSortMenuOpen,
    handleNameSortMenuClose,
    setFilterField,
    setFilterValue,
    setEmailSearch,

    // Renderers
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
    renderFilterFieldSelect,
    renderDynamicSearchInput,
  };
};
